import _extends from 'babel-runtime/helpers/extends';
import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { mediaActions, mediaSelectors } from '../redux/media-reducer';
import { mediumLightGrey } from '../styles/colors';
import { IMAGE_SIZE, MEDIA_TYPE, parseMediaContent, transparentPxImageSrc } from '../utils/media-utils';
import { MagnifierPlusIcon } from '../assets/MagnifierPlusIcon';

/**
 * ContentImage is for displaying images specified by one of the ways RS
 *  describes images in the content.  It can accept either an array of
 *  imageInfos or a single imageInfo.  An imageInfo looks like one of these:
 *
 *   {
 *     "media_uri": "urn:rsa:0100000000400200221728",
 *     "type": "picture_max_width_1080"
 *   }
 * or
 *   {
 *     "media_uri": "urn:biblio:assets/fbdceaa1-a87d-4364-ba93-eae95bbc5ddc",
 *     "type": "picture_max_width_320"
 *   }
 * or
 *   {
 *     "media_uri": "urn:rsa:0400000010020200570101pt-BR",
 *     "type": "picture_original"
 *   }
 * or
 *   {
 *     "media_uri": "urn:puddle:8d68ed0905aaa87a339381fdae457ad9a1aa26dd",
 *     "type": "picture_max_width_640"
 *   }
 *
 * The different urns represent different sources for the images.  They are parsed
 *  and converted into URIs by media-utils/parseMediaContent.
 *
 *
 *  SIZING:  ContentImage has four sizing options, all of which are about how it
 *   fills its container.  The image's native aspect ratio will ALWAYS be preserved.
 *
 *   * no sizing prop means "make the image fit inside the container so that it is 100% visible".
 *        It will stick to the top-left corner.
 *
 *   * `autoFill` means "fill the entire container withthe image, allowing overflow to right
 *        or bottom if the aspect ratio of the container does not match that of the image".
 *
 *   * `fitWidth` and `fitHeight` props are mutually exclusive and mean "fit the image
 *        to the container's width (or height) and ignore the other container dimension".
 **/

var ImageDiv = styled.div(function (props) {
  var style = {
    label: 'ImageDiv',
    position: 'relative',
    display: 'inline-block',
    maxWidth: '100%',
    width: props.displayWidth,
    height: props.displayHeight,
    overflow: 'hidden',
    ' img': {
      width: props.displayWidth,
      height: props.displayHeight
    }
  };
  if (props.displayHeight === undefined) {
    // means it has not been fully initialized so hide out
    style.opacity = 0;
    style[' img'] = {
      width: 1,
      height: 1
    };
  }
  if (props.rounded) {
    style.borderRadius = 6;
  }
  if (props.isOverlayTop) {
    style.position = 'absolute';
    style.top = 0;
    style.left = 0;
    style.zIndex = 1;
  }
  if (props.zoomed) {
    style.cursor = 'zoom-out';
  } else if (props.clickable) {
    style.cursor = 'pointer';
  } else if (props.zoomable) {
    style.cursor = 'zoom-in';
  }
  return style;
});

var ZoomBackground = styled.div({
  position: 'fixed',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: '10000',
  backgroundColor: 'rgba(0, 0, 0, 0.45)',
  cursor: 'zoom-out'
});

var RightCorner = function RightCorner() {
  return React.createElement(
    'svg',
    {
      xmlns: 'http://www.w3.org/2000/svg',
      width: '100%',
      height: '100%',
      preserveAspectRatio: 'none',
      viewBox: '0 0 1060 1060'
    },
    React.createElement('path', { fill: '#fff', opacity: '0.7', d: 'M 0 0 l 1060 1060 l 0 -1060 z' })
  );
};

var CornerButtonIconContainer = styled.div({
  position: 'absolute',
  top: 6,
  right: 6,
  width: 24,
  height: 24,
  mixBlendMode: 'multiply'
});

var ZoomButton = styled.div(function (props) {
  var style = {
    label: 'ZoomButton',
    position: 'absolute',
    right: 0,
    top: 0,
    height: 60,
    width: 60,
    cursor: 'zoom-in'
  };
  return style;
});

//***************************************************************************************

var ContentImageBase = function (_PureComponent) {
  _inherits(ContentImageBase, _PureComponent);

  function ContentImageBase(props) {
    _classCallCheck(this, ContentImageBase);

    var _this = _possibleConstructorReturn(this, _PureComponent.call(this, props));

    _this.handleResize = function () {
      if (_this.resizeTimeoutRef) {
        clearTimeout(_this.resizeTimeoutRef);
        _this.resizeTimeoutRef = null;
      }
      _this.resizeTimeoutRef = setTimeout(function () {
        _this.setState(_this.calcSize(_this.state.imageAspect));
      }, 10);
    };

    _this.handleImageSizeKnown = function (e) {
      _this.imageElem.removeEventListener('load', _this.handleImageSizeKnown);
      var imageNaturalAspect = _this.imageElem.naturalHeight / _this.imageElem.naturalWidth;
      var newSize = _this.calcSize(imageNaturalAspect);
      if (_this.props.onImageAspectRatioKnown) {
        _this.props.onImageAspectRatioKnown(imageNaturalAspect, newSize);
      }
      _this.setState(_extends({
        imageAspect: imageNaturalAspect
      }, newSize));
    };

    _this.handleImageElementCreated = function (imageElem) {
      if (!imageElem) {
        return;
      }
      _this.imageElem = imageElem;
      _this.imageElem.addEventListener('load', _this.handleImageSizeKnown);
    };

    _this.getImageInfo = function () {
      var imageRes = _this.props.imageResolution;
      var imageExtension = _this.props.imageExtension ? _this.props.imageExtension : 'jpg';
      if (_this.props.isOverlayImage) {
        imageRes = IMAGE_SIZE.LARGE;
        imageExtension = 'png';
      }
      if (_this.state.imageZoomed) {
        imageRes = IMAGE_SIZE.LARGE;
      }
      var parsedImageInfoArray = parseMediaContent({
        mediaInfoOrArray: _this.props.images,
        mediaDescriptor: imageRes,
        mediaType: MEDIA_TYPE.IMAGE,
        fileExtension: imageExtension,
        errorHandler: _this.props.handleError,
        isOverlayImage: _this.props.isOverlayImage,
        isWebi: _this.props.isWebi
      });
      if (parsedImageInfoArray && parsedImageInfoArray.length > 0) {
        return parsedImageInfoArray[0];
      }
      return null;
    };

    _this.installImage = function (imageInfo) {
      var newState = {
        imageAspect: 0,
        imageHeight: undefined,
        imageWidth: undefined,
        imageUri: null,
        loadingFromPuddle: false,
        puddleId: undefined
      };
      if (_this.imageElem) {
        _this.imageElem.removeEventListener('load', _this.handleImageSizeKnown);
        _this.imageElem = null;
      }
      if (imageInfo.encryptedPuddleId) {
        newState.puddleId = imageInfo.encryptedPuddleId;
        newState.imageUri = _this.props.loadedPuddleImageUri(imageInfo.encryptedPuddleId);
        if (!newState.imageUri) {
          _this.props.loadResourceFromPuddle(imageInfo.encryptedPuddleId);
          newState.loadingFromPuddle = true;
        }
      } else {
        newState.imageUri = imageInfo.mediaUri;
      }
      _this.setState(newState);
    };

    _this.imageClickHandler = function (e) {
      // if it is zoomed then all we do is unzoom
      if (_this.state.imageZoomed) {
        e.stopPropagation();
        _this.toggleImageZoom();
      } else {
        if (_this.props.onClick) {
          e.stopPropagation();
          // if unzoomed and it has an onClick then do the onClick,
          //  zooming will be handled by the corner icon
          _this.props.onClick(e);
        } else if (_this.props.withZoom) {
          // regular zooming with no corner icon
          e.stopPropagation();
          _this.toggleImageZoom();
        }
      }
    };

    _this.handleBackgroundClick = function (e) {
      // we only want this if they click the background, not the picture
      if (e.target === e.currentTarget) {
        _this.toggleImageZoom();
      }
    };

    _this.toggleImageZoom = function () {
      // when toggling image zoom, also set the image dimensions
      //  so it does not draw until it is properly resized
      _this.setState({
        imageZoomed: !_this.state.imageZoomed,
        imageHeight: undefined,
        imageWidth: undefined
      });
    };

    _this.state = {
      imageUri: null,
      imageAspect: 0,
      imageHeight: undefined,
      imageWidth: undefined,
      imageZoomed: false,
      loadingFromPuddle: false,
      puddleId: undefined
    };
    _this.containerRef = React.createRef();
    _this.imageElem = null;
    _this.resizeTimeoutRef = null;
    return _this;
  }

  ContentImageBase.prototype.componentDidMount = function componentDidMount() {
    var parsedImageInfo = this.getImageInfo();
    if (parsedImageInfo) {
      this.installImage(parsedImageInfo);
    }
    window.addEventListener('resize', this.handleResize);
  };

  ContentImageBase.prototype.componentWillUnmount = function componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimeoutRef) {
      clearTimeout(this.resizeTimeoutRef);
      this.resizeTimeoutRef = null;
    }
    if (this.imageElem) {
      this.imageElem.removeEventListener('load', this.handleImageSizeKnown);
    }
  };

  ContentImageBase.prototype.componentDidUpdate = function componentDidUpdate() {
    // handle two cases:  first, if loading from puddle, see if it is done loading
    //  second, if not loading, check to see if the image has changed and install if so
    if (this.state.loadingFromPuddle) {
      var loadedResource = this.props.loadedPuddleImageUri(this.state.puddleId);
      if (loadedResource) {
        this.setState({
          imageUri: loadedResource,
          loadingFromPuddle: false
        });
      }
    } else {
      var parsedImageInfo = this.getImageInfo();
      if (!parsedImageInfo) {
        return;
      }
      if (parsedImageInfo.mediaUri !== this.state.imageUri || parsedImageInfo.encryptedPuddleId !== this.state.puddleId) {
        this.installImage(parsedImageInfo);
        return;
      }
      // this is a test to see if the containing view has changed size for some
      //  reason other than window resizing.  If you are changing the
      //  size of the containing view, it might be good to add a (random, unused)
      //  prop to the ContentImage which triggers re-render when that
      //  containing view's size changes - so that we get to this code.
      if (!this.resizeTimeoutRef && this.state.imageAspect !== 0 && !this.state.imageZoomed) {
        var calculatedSize = this.calcSize(this.state.imageAspect);
        if (calculatedSize.imageWidth !== this.state.imageWidth || calculatedSize.imageHeight !== this.state.imageHeight) {
          this.setState(calculatedSize);
        }
      }
    }
  };

  ContentImageBase.prototype.calcSize = function calcSize(naturalAspect) {
    var calcWidth = undefined;
    var calcHeight = undefined;
    if (this.state.imageZoomed) {
      var windowAspect = window.innerHeight / window.innerWidth;
      var IMAGE_PERCENT = 0.95;
      if (windowAspect > naturalAspect) {
        // window taller than image
        calcWidth = IMAGE_PERCENT * window.innerWidth;
        calcHeight = calcWidth * naturalAspect;
      } else {
        // window wider than image
        calcHeight = IMAGE_PERCENT * window.innerHeight;
        calcWidth = calcHeight / naturalAspect;
      }
    } else {
      if (this.containerRef.current) {
        var parentElem = this.containerRef.current.parentElement;
        var containerRect = parentElem.getBoundingClientRect();
        var containerAspect = containerRect.height / containerRect.width;
        if (this.props.autoFill) {
          // this makes sure the container is totally full
          if (containerAspect > naturalAspect) {
            // container taller than image, make image very tall
            calcWidth = containerRect.height / naturalAspect;
            calcHeight = containerRect.height;
          } else {
            // container wider than image, make image very wide
            calcWidth = containerRect.width;
            calcHeight = containerRect.width * naturalAspect;
          }
        } else {
          if (!this.props.fitHeight && (containerAspect > naturalAspect || this.props.fitWidth)) {
            // container taller than image or fitting to width
            calcWidth = containerRect.width;
            calcHeight = calcWidth * naturalAspect;
          } else {
            // container wider than image or fitting to height
            calcHeight = containerRect.height;
            calcWidth = calcHeight / naturalAspect;
          }
        }
      }
    }
    return {
      imageHeight: calcHeight,
      imageWidth: calcWidth
    };
  };

  ContentImageBase.prototype.render = function render() {
    var imageUri = this.state.imageUri;
    if (this.state.loadingFromPuddle) {
      imageUri = transparentPxImageSrc;
    }
    if (!imageUri) {
      return null;
    }

    // only show zoom button if the image has an onClick handler, else
    //  the whole image is available for clicking to zoom
    var zoomButton = void 0;
    if (this.props.withZoom && this.props.onClick && !this.state.imageZoomed) {
      zoomButton = React.createElement(
        ZoomButton,
        { onClick: this.toggleImageZoom },
        React.createElement(RightCorner, null),
        React.createElement(
          CornerButtonIconContainer,
          null,
          React.createElement(MagnifierPlusIcon, { fillColor: mediumLightGrey })
        )
      );
    }

    // the key on the img fixes an issue where react does not call the ref (handleImageElementCreated)
    //  even if the imageUri changes
    var imageDisp = React.createElement(
      ImageDiv,
      {
        'data-qa': 'ContentImage',
        ref: this.containerRef,
        imageUri: imageUri,
        displayHeight: this.state.imageHeight,
        displayWidth: this.state.imageWidth,
        onClick: this.imageClickHandler,
        rounded: this.props.rounded,
        isOverlayTop: this.props.isOverlayTop,
        zoomable: this.props.withZoom,
        zoomed: this.state.imageZoomed,
        clickable: !!this.props.onClick
      },
      React.createElement('img', { key: imageUri, ref: this.handleImageElementCreated, src: imageUri, alt: '' }),
      zoomButton
    );

    if (this.state.imageZoomed) {
      return React.createElement(
        ZoomBackground,
        { onClick: this.handleBackgroundClick },
        imageDisp
      );
    } else {
      return imageDisp;
    }
  };

  return ContentImageBase;
}(PureComponent);

ContentImageBase.propTypes = {
  // are the corners rounded?
  rounded: PropTypes.bool,

  // should the image be zoomable?
  withZoom: PropTypes.bool,

  // autoFill means make the image big enough to completely fill
  //  its container, with overflow on bottom or right depending on
  //  the aspect ratios of the container and the image
  autoFill: PropTypes.bool,

  // fitHeight and fitWidth are for if we want to only use height or width
  //  of the ContentImage's container for sizing.  The other container dimension will be ignored.
  fitHeight: PropTypes.bool,
  fitWidth: function fitWidth(props) {
    if (props.fitHeight && props.fitWidth || props.fitHeight && props.autoFill || props.fitWidth && props.autoFill) {
      return new Error('invalid fitWidth/fitHeight/autoFill prop on ContentImage');
    }
  },

  // array of imageInfos or a single one
  images: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),

  // which one of the types from the images array do we want?
  //  examples are "picture_max_width_320" or "main"
  imageResolution: PropTypes.string,

  // isOverlayImage and isOverlayTop are about an (obsolete) RSA feature
  //  where there are transparent images that stack, and one is on top
  isOverlayImage: PropTypes.bool,
  isOverlayTop: PropTypes.bool,

  // if you don't want 'jpg' you have to say so here
  imageExtension: PropTypes.string,

  onClick: PropTypes.func,

  // onImageAspectRatioKnown is for if clients want to plan for the image
  //  it is called with one arg, the image aspect ratio, which is
  //  the image height / image width
  onImageAspectRatioKnown: PropTypes.func
};

var mapStateToProps = function mapStateToProps(state) {
  return {
    loadedPuddleImageUri: function loadedPuddleImageUri(puddleId) {
      return mediaSelectors.getLoadedResource(state, puddleId);
    },
    isWebi: mediaSelectors.isWebi(state)
  };
};

var wrappedActions = {
  loadResourceFromPuddle: mediaActions.loadResourceFromPuddle,
  handleError: mediaActions.handleError
};

export var ContentImage = connect(mapStateToProps, wrappedActions)(ContentImageBase);